import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit3, Trash2, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products");
      setProducts(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this product permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const toggleHidden = async (p) => {
    try {
      const payload = {
        sku: p.sku, name: p.name, category: p.category, tagline: p.tagline || "",
        description: p.description || "", price_inr: p.price_inr, price_usd: p.price_usd,
        image: p.image, images: p.images || [], fabric: p.fabric || "", care: p.care || "",
        materials: p.materials || [], sizes: p.sizes || [], colors: p.colors || [],
        stock: p.stock, low_stock_threshold: p.low_stock_threshold || 5,
        available: p.available !== false, hidden: !p.hidden,
      };
      await api.put(`/admin/products/${p.id}`, payload);
      toast.success(p.hidden ? "Published." : "Hidden from store.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="eyebrow text-silver/70 mb-2">Catalogue</div>
          <h1 className="font-serif text-white text-3xl md:text-4xl">Products.</h1>
        </div>
        <Link
          to="/atelier-admin/products/new"
          data-testid="admin-add-product"
          className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-[11px] tracking-[0.32em] uppercase hover:bg-silver"
        >
          <Plus size={14} /> Add product
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c.toLowerCase()}`}
            className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase border ${
              filter === c ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-white/40"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-white/50">No products.</div>
      ) : (
        <div className="border border-white/8 bg-midnight overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.28em] uppercase text-silver/70 border-b border-white/8">
                <th className="text-left py-3 px-4">Product</th>
                <th className="text-left py-3 px-4">SKU</th>
                <th className="text-left py-3 px-4">Category</th>
                <th className="text-right py-3 px-4">Price (INR)</th>
                <th className="text-right py-3 px-4">Stock</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-product-row-${p.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {p.image && <img src={p.image} alt="" className="w-10 h-12 object-cover bg-midnight-panel" />}
                      <div>
                        <div className="font-serif text-white">{p.name}</div>
                        <div className="text-[10px] text-white/40">{p.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-white/70 text-xs">{p.sku}</td>
                  <td className="py-3 px-4 text-white/70 text-xs">{p.category}</td>
                  <td className="py-3 px-4 text-right text-silver">{new Intl.NumberFormat("en-IN").format(p.price_inr)}</td>
                  <td className="py-3 px-4 text-right">
                    <span className={p.stock <= (p.low_stock_threshold || 5) ? "text-silver" : "text-white/70"}>{p.stock}</span>
                  </td>
                  <td className="py-3 px-4">
                    {p.hidden ? (
                      <span className="text-[10px] tracking-[0.28em] uppercase text-white/40 border border-white/15 px-2 py-1">Hidden</span>
                    ) : (
                      <span className="text-[10px] tracking-[0.28em] uppercase text-silver border border-silver/40 px-2 py-1">Live</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/atelier-admin/products/${p.id}/edit`}
                        data-testid={`edit-${p.id}`}
                        className="p-2 text-white/70 hover:text-white"
                      ><Edit3 size={14} /></Link>
                      <button
                        onClick={() => toggleHidden(p)}
                        data-testid={`hide-${p.id}`}
                        className="p-2 text-white/70 hover:text-white"
                        aria-label="Toggle visibility"
                      >{p.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                      <button
                        onClick={() => remove(p.id)}
                        data-testid={`delete-${p.id}`}
                        className="p-2 text-white/50 hover:text-white"
                        aria-label="Delete"
                      ><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
